export interface GetDocTypeMappingParams {
  offset?: number;
  count?: number;
  area?: string;
  category?: string;
}

export interface Field {
  name: string;
  format?: string;
  dataType?: string;
}

export interface FieldMapping {
  source: Field;
  target: Field;
}

export interface CreateDocTypeMappingRequest {
  modelDocType: string;
  docTypeCode: string;
  fields: FieldMapping[];
  areas?: string[];
  categories?: string[];
}

export interface UpdateDocTypeMappingRequest {
  modelDocType?: string;
  docTypeCode?: string;
  fields?: FieldMapping[];
  areasToAdd?: string[];
  areasToRemove?: string[];
  categoriesToAdd?: string[];
  categoriesToRemove?: string[];
  version: number;
}

export interface DocTypeMappingResponse extends CreateDocTypeMappingRequest {
  id: string;
  createdAt: string;
  updatedAt?: string;
  version: number;
}

export interface KeyField {
  key: string;
  value: string;
}

export interface MapRequest {
  modelDocType?: string;
  docTypeCode?: string;
  keyFields: KeyField[];
}

export interface MapItem {
  id: string;
  modelDocType: string;
  docTypeCode: string;
  keyFields: KeyField[];
}

export interface MapResponse {
  mapItems: MapItem[];
}

export class DocTypeMappingSdk {
  private baseURL: string;

  /**
   * Constructor
   * @param baseURL the base URL for the document analysis service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseURL = baseURL;
  }

  /**
   *
   * @param endpoint
   * @param options
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<any> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.message}`);
    }

    return response;
  }

  /**
   * Retrieves a list of document type mappings
   * @param tenantId the tenant identifier
   * @param params optional parameters for filtering the results
   * @returns a list of document type mappings
   */
  async getDocTypeMappings(
    tenantId: string,
    params: GetDocTypeMappingParams = {}
  ): Promise<DocTypeMappingResponse[]> {
    const query = new URLSearchParams(params as any).toString();
    const endpoint = `/configurations/docType/mappings?${query}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        tenantId: tenantId
      }
    });

    return response.json();
  }

  /**
   * Retrieves a document type mapping
   * @param tenantId the tenant identifier
   * @param id the document type mapping identifier
   * @returns the document type mapping details
   */
  async getDocTypeMapping(tenantId: string, id: string): Promise<DocTypeMappingResponse> {
    const endpoint = `/configurations/docType/mappings/${id}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        tenantId: tenantId
      }
    });

    return response.json();
  }

  /**
   * Creates a new document type mapping
   * @param tenantId the tenant identifier
   * @param data the document type mapping details
   * @returns the created document type mapping
   */
  async createDocTypeMapping(
    tenantId: string,
    data: CreateDocTypeMappingRequest
  ): Promise<DocTypeMappingResponse> {
    const endpoint = `/configurations/docType/mappings`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        tenantId: tenantId
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Updates an existing document type mapping
   * @param tenantId the tenant identifier
   * @param id the document type mapping identifier
   * @param data the document type mapping update details
   * @returns the updated document type mapping
   */

  /**
   *
   * @param tenantId
   * @param id
   * @param data
   */
  async updateDocTypeMapping(
    tenantId: string,
    id: string,
    data: UpdateDocTypeMappingRequest
  ): Promise<DocTypeMappingResponse> {
    const endpoint = `/configurations/docType/mappings/${id}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'PATCH',
      headers: {
        'content-type': 'application/json',
        tenantId: tenantId
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Deletes a document type mapping by ID
   * @param tenantId the tenant identifier
   * @param id the document type mapping identifier
   */
  async deleteDocTypeMapping(tenantId: string, id: string): Promise<void> {
    const endpoint = `/configurations/docType/mappings/${id}`;
    await this.fetchWrapper(endpoint, {
      method: 'DELETE',
      headers: {
        tenantId: tenantId
      }
    });
  }

  /**
   * Maps key fields
   * @param tenantId the tenant identifier
   * @param data a mapping request
   * @param accessToken the access token for authentication (optional)
   * @returns a mapping response
   */
  async mapKeyFields(
    tenantId: string,
    data: MapRequest,
    accessToken?: string | null
  ): Promise<MapResponse> {
    const endpoint = `/documents/map`;
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      tenantId: tenantId
    };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });

    return response.json() as Promise<MapResponse>;
  }
}
